import { useEffect, useMemo, useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import styles from './DialogBlockModal.module.css';

// Composes a dialog block: CHARACTER (required, uppercase, autocomplete from
// known), optional parenthetical, dialog body. Submits a multi-line string the
// editor inserts as `@@CHR …`, optional `@@PAR (…)`, `@@DLG …`.
export default function DialogBlockModal({
  isOpen,
  onClose,
  onSubmit,
  known = [],
}) {
  const [character, setCharacter] = useState('');
  const [paren, setParen] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setCharacter('');
    setParen('');
    setBody('');
    setError(null);
  }, [isOpen]);

  const suggestions = useMemo(() => {
    const q = character.trim().toUpperCase();
    if (!q) return known.slice(0, 6);
    return known.filter((n) => n.includes(q)).slice(0, 6);
  }, [character, known]);

  const submit = () => {
    const c = character.trim().toUpperCase();
    const b = body.trim();
    if (!c) {
      setError('Character is required');
      return;
    }
    if (!b) {
      setError('Dialog body is required');
      return;
    }
    let p = paren.trim();
    if (p) {
      p = p.replace(/^\(+|\)+$/g, '').trim();
    }
    onSubmit({ character: c, paren: p, body: b });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Insert dialog block"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit}>Insert</Button>
        </>
      }
    >
      <Input
        label="Character"
        placeholder="e.g. JOAO GRILO"
        value={character}
        onChange={(e) => { setCharacter(e.target.value); setError(null); }}
        autoFocus
      />
      {suggestions.length > 0 && (
        <ul className={styles.list}>
          {suggestions.map((n) => (
            <li
              key={n}
              className={styles.row}
              onClick={() => setCharacter(n)}
            >
              <span>{n}</span>
              <span className={styles.rowMeta}>known</span>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.spacer} />

      <Input
        id="dlg-paren"
        label="Parenthetical (optional)"
        placeholder="e.g. sussurrando"
        value={paren}
        onChange={(e) => setParen(e.target.value)}
      />

      <div className={styles.spacer} />

      <label className={styles.bodyLabel} htmlFor="dlg-body">Dialog</label>
      <textarea
        id="dlg-body"
        className={styles.textarea}
        placeholder="What does the character say?"
        value={body}
        onChange={(e) => { setBody(e.target.value); setError(null); }}
        rows={4}
      />

      {error && <p className={styles.error}>{error}</p>}
    </Modal>
  );
}
